import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { useLanguage } from '../hooks/useLanguage';
import { Menu, X, Globe } from 'lucide-react';
import BookingModal from './BookingModal';
import { useLocation } from 'react-router-dom';

const Header = () => {
  const { t, language, changeLanguage } = useLanguage();
  const location = useLocation();
  const isHomepage = location.pathname === '/';
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t('nav.offers'), href: '/#offers' },
    { name: t('nav.howItWorks'), href: '/#how-it-works' },
    { name: t('nav.gallery'), href: '/#gallery' },
    { name: t('nav.testimonials'), href: '/#testimonials' },
    { name: t('nav.contact'), href: '/contact' },
  ];

  return (
    <>
      <header 
        style={{ top: 'var(--wwn-promo-bar-height, 0px)' }}
        className={`fixed w-full z-50 transition-all duration-300 ${
          (!isHomepage || isScrolled) ? 'bg-primary-950/95 backdrop-blur-md shadow-lg py-3' : 'bg-transparent py-5'
        }`}
      >
        <div className="container mx-auto px-4 md:px-6 flex justify-between items-center">
          {/* Logo */}
          <button 
            onClick={() => window.location.href = '/'}
            className="flex items-center gap-2 cursor-pointer"
          >
            <img 
              src="/images/logo-wownautic.png" 
              alt="Wow Nautic" 
              className="h-14 md:h-20 w-auto object-contain"
            />
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="/#offers" className="text-white hover:text-accent-400 transition-colors font-medium">
              {t('nav.offers')}
            </a>
            <a href="/#gallery" className="text-white hover:text-accent-400 transition-colors font-medium">
              {t('nav.gallery')}
            </a>
            <a href="/#testimonials" className="text-white hover:text-accent-400 transition-colors font-medium">
              {t('nav.testimonials')}
            </a>
            <a href="/contact" className="text-white hover:text-accent-400 transition-colors font-medium">
              {t('nav.contact')}
            </a>
          </nav>

          {/* Actions */}
          <div className="hidden md:flex items-center gap-4">
            <div className="relative group">
              <button className="flex items-center gap-1 text-white hover:text-accent-400 transition-colors">
                <Globe className="w-4 h-4" />
                <span className="uppercase text-sm font-medium">{language}</span>
              </button>
              <div className="absolute right-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="bg-white rounded-md shadow-xl overflow-hidden min-w-[120px]">
                  {['en', 'es', 'fr'].map((lang) => (
                    <button
                      key={lang}
                      onClick={() => changeLanguage(lang)}
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                        language === lang ? 'bg-gray-50 font-bold text-primary-950' : 'text-gray-700'
                      }`}
                    >
                      {lang === 'en' ? 'English' : lang === 'es' ? 'Español' : 'Français'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <Button 
              variant="default" 
              className="bg-accent-500 hover:bg-accent-600 text-white font-semibold rounded-full px-6"
              onClick={() => window.location.href = '/#offers'}
            >
              {t('nav.bookNow')}
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-primary-950/95 backdrop-blur-xl border-t border-white/10 p-4 flex flex-col gap-4 animate-in slide-in-from-top-5">
            {navLinks.map((link) => (
              <a 
                key={link.name} 
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="text-white text-lg font-medium py-2 border-b border-white/5"
              >
                {link.name}
              </a>
            ))}
            
            <div className="flex gap-4 py-4">
              {['en', 'es', 'fr'].map((lang) => (
                <button
                  key={lang}
                  onClick={() => {
                    changeLanguage(lang);
                    setMobileMenuOpen(false);
                  }}
                  className={`px-3 py-1 rounded-md text-sm font-bold uppercase ${
                    language === lang 
                      ? 'bg-accent-500 text-white' 
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>

            <Button 
              className="w-full bg-accent-500 hover:bg-accent-600"
              onClick={() => {
                setMobileMenuOpen(false);
                window.location.href = '/#offers';
              }}
            >
              {t('nav.bookNow')}
            </Button>
          </div>
        )}
      </header>
      
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </>
  );
};

export default Header;
